if (!customElements.get('category-product-carousel')) {
  customElements.define('category-product-carousel', class extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('slider-element');
      this.scrollbar = this.querySelector('input[type="range"]');
      this.nextButton = this.querySelector('.category-product-carousel__next');
      this.previousButton = this.querySelector('.category-product-carousel__previous');
      if (!this.slider || !this.scrollbar) return;

      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.update = this.update.bind(this);
      this.slider.addEventListener('scroll', this.update, { passive: true, signal });
      ['slider:previousStatus', 'slider:nextStatus'].forEach((type) => {
        this.addEventListener(type, (event) => {
          if (event.target === this.slider) this.update();
        }, { signal });
      });
      this.scrollbar.addEventListener('input', () => {
        const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
        const direction = getComputedStyle(this.slider).direction === 'rtl' ? -1 : 1;
        // Range dragging should follow the pointer, without scroll-snap fighting it.
        this.slider.style.scrollSnapType = 'none';
        this.slider.scrollLeft = direction * maxScroll * Number(this.scrollbar.value) / 1000;
      }, { signal });
      this.scrollbar.addEventListener('change', () => {
        this.slider.style.removeProperty('scroll-snap-type');
      }, { signal });

      this.observer?.disconnect();
      this.observer = new ResizeObserver(this.update);
      this.observer.observe(this.slider);
      this.observer.observe(this.querySelector('motion-list'));
      this.setAttribute('data-enhanced', '');
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.removeAttribute('data-enhanced');
    }

    update() {
      const width = this.slider.clientWidth;
      const total = this.slider.scrollWidth;
      const maxScroll = Math.max(0, total - width);
      const position = Math.abs(this.slider.scrollLeft);
      this.scrollbar.hidden = maxScroll <= 1;
      this.scrollbar.value = maxScroll > 0 ? Math.round(position / maxScroll * 1000) : 0;
      if (this.previousButton) {
        this.previousButton.hidden = maxScroll <= 1 || (window.innerWidth > 767 && position <= 1);
        this.previousButton.disabled = position <= 1;
      }
      this.toggleAttribute('data-at-end', maxScroll - position <= 1);
      if (this.nextButton) {
        this.nextButton.hidden = maxScroll <= 1;
        this.nextButton.disabled = maxScroll - position <= 1;
      }
    }
  });
}

if (!customElements.get('category-product-tabs')) {
  customElements.define('category-product-tabs', class extends HTMLElement {
    connectedCallback() {
      this.navigation = this.querySelector('tabs-element');
      if (!this.navigation) return;
      this.buttons = Array.from(this.navigation.querySelectorAll('[role="tab"]'));
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.sync = () => {
        const selected = this.navigation.selectedIndex || 0;
        this.buttons.forEach((button, index) => {
          // Keep the theme's selected tab focusable for keyboard navigation.
          button.disabled = false;
          button.setAttribute('aria-selected', String(index === selected));
          button.tabIndex = index === selected ? 0 : -1;
        });
        const button = this.buttons[selected];
        if (button) {
          const tab = button.getBoundingClientRect();
          const nav = this.navigation.getBoundingClientRect();
          if (tab.left < nav.left || tab.right > nav.right) {
            this.navigation.scrollLeft += tab.left - nav.left - (nav.width - tab.width) / 2;
          }
        }
      };

      this.navigation.addEventListener('keydown', (event) => {
        const index = this.buttons.indexOf(event.target);
        if (index < 0) return;
        const rtl = getComputedStyle(this.navigation).direction === 'rtl';
        let next = index;
        if (event.key === 'ArrowRight') next += rtl ? -1 : 1;
        else if (event.key === 'ArrowLeft') next += rtl ? 1 : -1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.buttons.length - 1;
        else return;
        event.preventDefault();
        this.navigation.selectedIndex = (next + this.buttons.length) % this.buttons.length;
        this.sync();
        this.buttons[this.navigation.selectedIndex].focus();
      }, { signal });

      this.addEventListener('shopify:block:select', (event) => {
        const index = this.buttons.indexOf(event.target.closest('[role="tab"]'));
        if (index >= 0) {
          this.navigation.selectedIndex = index;
          this.sync();
        }
      }, { signal });

      this.observer?.disconnect();
      this.observer = new MutationObserver(this.sync);
      this.observer.observe(this.navigation, { attributes: true, attributeFilter: ['selected-index'] });
      this.sync();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }
  });
}
